import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Calendar, ChevronLeft, ChevronRight, Save, Wand2,
  ClipboardList, Users, Settings as SettingsIcon,
  X, AlertTriangle, Edit2, Plus
} from 'lucide-react';
import { supabaseClient } from '../lib/supabaseClient';
import { sortEmployeesDefault } from '../lib/employeeSorter';
import type { AdminEmployee, LeaveRequest } from '../types/models';
import {
  ShiftType,
  ShiftCategory,
  DayOfWeek,
  SHIFT_TYPE_LABELS,
  SHIFT_TYPE_COLORS,
  SHIFT_CATEGORY_LABELS,
  DAY_OF_WEEK_LABELS,
  PREFERENCE_TYPE_LABELS,
  PreferenceType,
  EmployeeShiftCondition,
  ShiftPreference,
  ShiftSchedule,
  ShiftSettings,
  toShiftCondition,
  toShiftPreference,
  toShiftSchedule,
  toShiftSettings,
} from '../types/shift';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface ShiftManagementTabProps {
  employees: AdminEmployee[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
type SubTab = 'schedule' | 'preferences' | 'conditions' | 'settings';

/** Shift types used in the schedule view */
const USED_SHIFT_TYPES: ShiftType[] = ['day', 'early', 'night', 'after_night', 'dayoff', 'paid', 'am'];

const ALL_SHIFT_TYPES: ShiftType[] = [
  'day', 'early', 'late', 'late_special', 'night', 'after_night',
  'dayoff', 'paid', 'am', 'pm', 'on_call',
];

const DAY_OF_WEEK_ORDER: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const DEFAULT_SETTINGS: ShiftSettings = {
  id: '',
  deadlineDay: 15,
  minNight: 2,
  minEarly: 2,
  idealEarly: 3,
  minDay: 1,
  idealDay: 2,
  targetJobType: '介護',
  monthlyOffDays31: 9,
  monthlyOffDaysOther: 8,
};

function getDaysInMonth(year: number, month: number): Date[] {
  const days: Date[] = [];
  const last = new Date(year, month, 0).getDate();
  for (let d = 1; d <= last; d++) {
    days.push(new Date(year, month - 1, d));
  }
  return days;
}

function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];
const WEEKDAY_MAP: Record<number, DayOfWeek> = {
  0: 'sun', 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat',
};

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ---------------------------------------------------------------------------
// generateShift algorithm (revised)
// ---------------------------------------------------------------------------
interface GenerateShiftInput {
  employees: AdminEmployee[];
  conditions: EmployeeShiftCondition[];
  preferences: ShiftPreference[];
  settings: ShiftSettings;
  year: number;
  month: number;
  approvedLeaves: LeaveRequest[];
}

interface GeneratedEntry {
  employeeId: string;
  scheduleDate: string;
  shiftType: ShiftType;
}

function generateShift(input: GenerateShiftInput): GeneratedEntry[] {
  const { employees, conditions, preferences, settings, year, month, approvedLeaves } = input;
  const days = getDaysInMonth(year, month);
  const daysInMonth = days.length;
  const condMap = new Map<string, EmployeeShiftCondition>();
  conditions.forEach(c => condMap.set(c.employeeId, c));

  // Matrix: employeeId -> dateStr -> ShiftType | null
  const matrix = new Map<string, Map<string, ShiftType | null>>();
  const activeEmps = employees.filter(e => e.status === 'active');
  activeEmps.forEach(e => {
    const dayMap = new Map<string, ShiftType | null>();
    days.forEach(d => dayMap.set(formatDate(d), null));
    matrix.set(e.id, dayMap);
  });

  // Determine required off-days per employee
  const requiredOffDays = new Map<string, number>();
  activeEmps.forEach(emp => {
    const cond = condMap.get(emp.id);
    if (!cond) {
      // Default: fulltime rules
      requiredOffDays.set(emp.id, daysInMonth === 31 ? settings.monthlyOffDays31 : settings.monthlyOffDaysOther);
      return;
    }
    if (cond.shiftCategory === 'night_only_part' || cond.shiftCategory === 'short_part' || cond.shiftCategory === 'fulltime_part') {
      // Part-timers: calculate from workDaysPerWeek
      if (cond.workDaysPerWeek) {
        const totalWorkDays = Math.round(cond.workDaysPerWeek * (daysInMonth / 7));
        requiredOffDays.set(emp.id, daysInMonth - totalWorkDays);
      } else {
        requiredOffDays.set(emp.id, daysInMonth === 31 ? settings.monthlyOffDays31 : settings.monthlyOffDaysOther);
      }
    } else {
      // Fulltime
      requiredOffDays.set(emp.id, daysInMonth === 31 ? settings.monthlyOffDays31 : settings.monthlyOffDaysOther);
    }
  });

  // Step 1: Apply preferences (highest priority)
  preferences.forEach(p => {
    const dayMap = matrix.get(p.employeeId);
    if (!dayMap) return;
    if (p.targetYear !== year || p.targetMonth !== month) return;
    if (p.preferenceType === 'paid') dayMap.set(p.preferenceDate, 'paid');
    else if (p.preferenceType === 'dayoff') dayMap.set(p.preferenceDate, 'dayoff');
    // night_request is just a request; don't lock in yet
  });

  // Step 2: Apply approved leave requests
  approvedLeaves.forEach(lr => {
    const dayMap = matrix.get(lr.employeeCode);
    if (!dayMap) return;
    const start = new Date(lr.startDate);
    const end = new Date(lr.endDate);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const ds = formatDate(d);
      if (dayMap.has(ds)) dayMap.set(ds, 'paid');
    }
  });

  // Step 3: Fixed off-days (regularOffDays) & weekend work
  activeEmps.forEach(emp => {
    const cond = condMap.get(emp.id);
    if (!cond) return;
    const dayMap = matrix.get(emp.id)!;
    const offDays = cond.regularOffDays ?? [];
    if (offDays.length === 0) return;

    // Mark all regular off days as dayoff first
    const weekendWorkDates: string[] = [];
    days.forEach(d => {
      const ds = formatDate(d);
      if (dayMap.get(ds) !== null) return; // already set by preference/leave
      const dow = WEEKDAY_MAP[d.getDay()];
      if (offDays.includes(dow)) {
        dayMap.set(ds, 'dayoff');
        // Track if this is a weekend work allowed day
        const allowed = cond.weekendWorkAllowedDays ?? [];
        if (allowed.includes(dow)) {
          weekendWorkDates.push(ds);
        }
      }
    });

    // Restore some to work based on monthlyWeekendWork
    const restoreCount = Math.min(cond.monthlyWeekendWork, weekendWorkDates.length);
    const shuffled = shuffleArray(weekendWorkDates);
    for (let i = 0; i < restoreCount; i++) {
      dayMap.set(shuffled[i], null); // clear back to unassigned
    }
  });

  // Step 4: Night-only part-timers
  activeEmps.forEach(emp => {
    const cond = condMap.get(emp.id);
    if (!cond || cond.shiftCategory !== 'night_only_part') return;
    const dayMap = matrix.get(emp.id)!;
    let nightCount = 0;
    const maxNight = cond.nightShiftMaxPerMonth ?? 99;
    const allowedDays = cond.nightShiftAllowedDays;

    days.forEach(d => {
      const ds = formatDate(d);
      if (dayMap.get(ds) !== null) return;
      if (nightCount >= maxNight) {
        dayMap.set(ds, 'dayoff');
        return;
      }
      const dow = WEEKDAY_MAP[d.getDay()];
      if (allowedDays && !allowedDays.includes(dow)) {
        dayMap.set(ds, 'dayoff');
        return;
      }
      dayMap.set(ds, 'night');
      nightCount++;
    });

    // Fill remaining unassigned as dayoff
    days.forEach(d => {
      const ds = formatDate(d);
      if (dayMap.get(ds) === null) dayMap.set(ds, 'dayoff');
    });
  });

  // Step 5: Night shift assignment for night_available employees
  const nightAvailableEmps = activeEmps.filter(e => {
    const c = condMap.get(e.id);
    return c && c.shiftCategory === 'night_available';
  });

  const nightCountMap = new Map<string, number>();
  nightAvailableEmps.forEach(e => nightCountMap.set(e.id, 0));

  // Count already assigned night shifts (from night_only_part)
  const nightOnlyPartEmps = activeEmps.filter(e => {
    const c = condMap.get(e.id);
    return c && c.shiftCategory === 'night_only_part';
  });

  days.forEach((d, idx) => {
    const ds = formatDate(d);
    // Count existing night coverage from night_only_part
    let nightCoveredCount = 0;
    nightOnlyPartEmps.forEach(e => {
      const dayMap = matrix.get(e.id)!;
      if (dayMap.get(ds) === 'night') nightCoveredCount++;
    });

    // Need at least min_night people on night shift
    const needed = Math.max(0, settings.minNight - nightCoveredCount);
    if (needed === 0) return;

    // 翌日(明け)と翌々日(公休)のセット可能性をチェック
    const next1Ds = idx + 1 < days.length ? formatDate(days[idx + 1]) : null;
    const next2Ds = idx + 2 < days.length ? formatDate(days[idx + 2]) : null;

    const dow = WEEKDAY_MAP[d.getDay()];
    const candidates = nightAvailableEmps.filter(e => {
      const dayMap = matrix.get(e.id)!;
      if (dayMap.get(ds) !== null) return false; // already assigned
      const cond = condMap.get(e.id)!;
      if (cond.nightShiftAllowedDays && !cond.nightShiftAllowedDays.includes(dow)) return false;
      const max = cond.nightShiftMaxPerMonth;
      if (max !== null && (nightCountMap.get(e.id) ?? 0) >= max) return false;

      // 連続夜勤を禁止: 前日が夜勤の人は除外
      const prevDay = new Date(d);
      prevDay.setDate(prevDay.getDate() - 1);
      const prev = dayMap.get(formatDate(prevDay));
      if (prev === 'night' || prev === 'after_night') return false;

      // 翌日が既に固定希望(paid/dayoff/night)なら明けセットできないので除外
      if (next1Ds) {
        const next1 = dayMap.get(next1Ds);
        if (next1 === 'paid' || next1 === 'night') return false;
      }
      // 翌々日が既にnightなら公休セットできないので除外（pad/dayoffは上書きしない=OK）
      if (next2Ds) {
        const next2 = dayMap.get(next2Ds);
        if (next2 === 'night') return false;
      }

      return true;
    });

    if (candidates.length === 0) return;

    // Sort: no-limit first, then fewest nights
    candidates.sort((a, b) => {
      const condA = condMap.get(a.id)!;
      const condB = condMap.get(b.id)!;
      const noLimitA = condA.nightShiftMaxPerMonth === null ? 0 : 1;
      const noLimitB = condB.nightShiftMaxPerMonth === null ? 0 : 1;
      if (noLimitA !== noLimitB) return noLimitA - noLimitB;
      return (nightCountMap.get(a.id) ?? 0) - (nightCountMap.get(b.id) ?? 0);
    });

    const assignCount = Math.min(needed, candidates.length);
    for (let i = 0; i < assignCount; i++) {
      const chosen = candidates[i];
      const dayMap = matrix.get(chosen.id)!;
      // 夜勤・明け・公休の3日セットで割当
      dayMap.set(ds, 'night');
      nightCountMap.set(chosen.id, (nightCountMap.get(chosen.id) ?? 0) + 1);
      if (next1Ds) {
        const cur1 = dayMap.get(next1Ds);
        // 翌日: 既にpaid/dayoffならそれ尊重、それ以外は明けに上書き
        if (cur1 !== 'paid' && cur1 !== 'dayoff' && cur1 !== 'night') {
          dayMap.set(next1Ds, 'after_night');
        }
      }
      if (next2Ds) {
        const cur2 = dayMap.get(next2Ds);
        // 翌々日: 既にpaid/dayoff/nightならそれ尊重、それ以外は公休に上書き
        if (cur2 !== 'paid' && cur2 !== 'dayoff' && cur2 !== 'night' && cur2 !== 'after_night') {
          dayMap.set(next2Ds, 'dayoff');
        }
      }
    }
  });

  // Step 6: Early shift assignment (target: ideal_early everyday, min_early sundays)
  const earlyCountMap = new Map<string, number>();
  activeEmps.forEach(e => earlyCountMap.set(e.id, 0));
  days.forEach(d => {
    const ds = formatDate(d);
    const dow = d.getDay();
    const isSunday = dow === 0;
    const targetEarly = isSunday ? settings.minEarly : settings.idealEarly;

    // Count existing early assignments
    let earlyCount = 0;
    activeEmps.forEach(emp => {
      const dayMap = matrix.get(emp.id)!;
      if (dayMap.get(ds) === 'early') {
        earlyCount++;
        earlyCountMap.set(emp.id, (earlyCountMap.get(emp.id) ?? 0) + 1);
      }
    });

    if (earlyCount >= targetEarly) return;

    const needed = targetEarly - earlyCount;
    const candidates = activeEmps.filter(emp => {
      const cond = condMap.get(emp.id);
      if (!cond) return false;
      if (cond.shiftCategory === 'night_only_part') return false;
      const dayMap = matrix.get(emp.id)!;
      if (dayMap.get(ds) !== null) return false;
      const allowed = cond.allowedShiftTypes;
      if (allowed && !allowed.includes('early')) return false;
      return true;
    });

    // 早番回数の少ない人を優先（公平性）
    candidates.sort((a, b) => (earlyCountMap.get(a.id) ?? 0) - (earlyCountMap.get(b.id) ?? 0));
    const assignCount = Math.min(needed, candidates.length);
    for (let i = 0; i < assignCount; i++) {
      matrix.get(candidates[i].id)!.set(ds, 'early');
      earlyCountMap.set(candidates[i].id, (earlyCountMap.get(candidates[i].id) ?? 0) + 1);
    }
  });

  // Step 7: Day/AM shift assignment for remaining empty slots
  activeEmps.forEach(emp => {
    const cond = condMap.get(emp.id);
    if (!cond) return;
    if (cond.shiftCategory === 'night_only_part') return;
    const dayMap = matrix.get(emp.id)!;
    const allowed = cond.allowedShiftTypes ?? ['day', 'early'];
    // Use day or am from allowed types (excluding night/dayoff/paid/early which are already handled)
    const dayTypes = allowed.filter(t =>
      t === 'day' || t === 'am'
    );
    const fallbackType: ShiftType = dayTypes.length > 0 ? dayTypes[0] : 'day';

    days.forEach(d => {
      const ds = formatDate(d);
      if (dayMap.get(ds) !== null) return;
      dayMap.set(ds, fallbackType);
    });
  });

  // Step 8: Off-day count adjustment - ensure correct number of off days
  activeEmps.forEach(emp => {
    const cond = condMap.get(emp.id);
    if (!cond || cond.shiftCategory === 'night_only_part') return;
    const dayMap = matrix.get(emp.id)!;
    const targetOff = requiredOffDays.get(emp.id) ?? (daysInMonth === 31 ? settings.monthlyOffDays31 : settings.monthlyOffDaysOther);

    // Count current off days (dayoff + paid)
    let currentOff = 0;
    days.forEach(d => {
      const ds = formatDate(d);
      const st = dayMap.get(ds);
      if (st === 'dayoff' || st === 'paid') currentOff++;
    });

    // If not enough off days, convert some work days to dayoff
    if (currentOff < targetOff) {
      const deficit = targetOff - currentOff;
      // Prefer converting day shifts on days with more coverage
      const workDates = days
        .filter(d => {
          const ds = formatDate(d);
          const st = dayMap.get(ds);
          return st !== null && st !== 'dayoff' && st !== 'paid' && st !== 'night' && st !== 'after_night';
        })
        .map(d => formatDate(d));

      const toConvert = shuffleArray(workDates).slice(0, deficit);
      toConvert.forEach(ds => dayMap.set(ds, 'dayoff'));
    }
  });

  // Step 9: Weekly work days check
  activeEmps.forEach(emp => {
    const cond = condMap.get(emp.id);
    if (!cond || !cond.workDaysPerWeek) return;
    const dayMap = matrix.get(emp.id)!;
    const maxPerWeek = cond.workDaysPerWeek;

    // Process week by week (Mon-Sun)
    let weekStart = new Date(year, month - 1, 1);
    const dayOfWeek = weekStart.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    weekStart = new Date(weekStart);
    weekStart.setDate(weekStart.getDate() + mondayOffset);

    while (weekStart.getMonth() <= month - 1 || (weekStart.getMonth() === 0 && month === 1)) {
      const weekDates: string[] = [];
      for (let i = 0; i < 7; i++) {
        const wd = new Date(weekStart);
        wd.setDate(wd.getDate() + i);
        const ds = formatDate(wd);
        if (dayMap.has(ds)) weekDates.push(ds);
      }

      const workDays = weekDates.filter(ds => {
        const st = dayMap.get(ds);
        return st !== null && st !== 'dayoff' && st !== 'paid';
      });

      if (workDays.length > maxPerWeek) {
        const excess = workDays.length - maxPerWeek;
        const reversed = [...workDays].reverse();
        for (let i = 0; i < excess; i++) {
          dayMap.set(reversed[i], 'dayoff');
        }
      }

      weekStart.setDate(weekStart.getDate() + 7);
      if (weekStart > days[days.length - 1]) break;
    }
  });

  // Build output
  const result: GeneratedEntry[] = [];
  matrix.forEach((dayMap, empId) => {
    dayMap.forEach((st, ds) => {
      if (st) {
        result.push({ employeeId: empId, scheduleDate: ds, shiftType: st });
      }
    });
  });

  return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const ShiftManagementTab: React.FC<ShiftManagementTabProps> = ({ employees }) => {
  // --- state ---
  const [subTab, setSubTab] = useState<SubTab>('schedule');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [schedules, setSchedules] = useState<ShiftSchedule[]>([]);
  const [preferences, setPreferences] = useState<ShiftPreference[]>([]);
  const [conditions, setConditions] = useState<EmployeeShiftCondition[]>([]);
  const [approvedLeaves, setApprovedLeaves] = useState<LeaveRequest[]>([]);
  const [shiftSettings, setShiftSettings] = useState<ShiftSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCell, setEditingCell] = useState<{ empId: string; date: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  // Conditions editing
  const [editingCondition, setEditingCondition] = useState<EmployeeShiftCondition | null>(null);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  // Settings editing
  const [settingsForm, setSettingsForm] = useState<ShiftSettings>(DEFAULT_SETTINGS);
  const [settingsSaving, setSettingsSaving] = useState(false);

  // Local schedule edits (unsaved)
  const [localEdits, setLocalEdits] = useState<Map<string, ShiftType>>(new Map());

  const days = useMemo(() => getDaysInMonth(year, month), [year, month]);

  // Filter employees by target_job_type OR has shift condition registered
  const filteredEmployees = useMemo(() => {
    const targetJobType = shiftSettings.targetJobType;
    const conditionEmployeeIds = new Set(conditions.map(c => c.employeeId));
    return sortEmployeesDefault(
      employees.filter(e =>
        e.status === 'active' &&
        (e.jobType === targetJobType || conditionEmployeeIds.has(e.id) || e.employmentType === 'その他')
      )
    );
  }, [employees, shiftSettings.targetJobType, conditions]);

  // For preferences tab, show all active employees
  const activeEmployees = useMemo(
    () => sortEmployeesDefault(employees.filter(e => e.status === 'active')),
    [employees]
  );

  // --- key helpers ---
  const cellKey = useCallback((empId: string, date: string) => `${empId}|${date}`, []);
  const getScheduleForCell = useCallback(
    (empId: string, date: string): ShiftType | null => {
      const key = cellKey(empId, date);
      if (localEdits.has(key)) return localEdits.get(key)!;
      const s = schedules.find(s => s.employeeId === empId && s.scheduleDate === date);
      return s ? s.shiftType : null;
    },
    [schedules, localEdits, cellKey]
  );

  // --- data fetching ---
  const fetchData = useCallback(async () => {
    if (!supabaseClient) return;
    setLoading(true);
    try {
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const [schedRes, prefRes, condRes, leaveRes, settingsRes] = await Promise.all([
        supabaseClient
          .from('shift_schedules')
          .select('*')
          .gte('schedule_date', startDate)
          .lte('schedule_date', endDate),
        supabaseClient
          .from('shift_preferences')
          .select('*')
          .eq('target_year', year)
          .eq('target_month', month),
        supabaseClient
          .from('employee_shift_conditions')
          .select('*'),
        supabaseClient
          .from('leave_requests')
          .select('*')
          .eq('status', 'approved')
          .gte('end_date', startDate)
          .lte('start_date', endDate),
        supabaseClient
          .from('shift_settings')
          .select('*')
          .single(),
      ]);

      if (schedRes.data) setSchedules(schedRes.data.map(toShiftSchedule));
      if (prefRes.data) setPreferences(prefRes.data.map(toShiftPreference));
      if (condRes.data) setConditions(condRes.data.map(toShiftCondition));
      if (leaveRes.data) {
        setApprovedLeaves(
          leaveRes.data.map((r: Record<string, unknown>) => ({
            id: String(r.id),
            employeeCode: String(r.employee_code ?? r.employee_id ?? ''),
            employeeName: String(r.employee_name ?? ''),
            startDate: String(r.start_date),
            endDate: String(r.end_date),
            status: r.status as 'approved',
            rejectionReason: (r.rejection_reason as string) ?? null,
            createdAt: String(r.created_at),
            updatedAt: String(r.updated_at),
            approvedAt: (r.approved_at as string) ?? null,
          }))
        );
      }
      if (settingsRes.data) {
        const s = toShiftSettings(settingsRes.data as Record<string, unknown>);
        setShiftSettings(s);
        setSettingsForm(s);
      }
      setLocalEdits(new Map());
    } catch (err) {
      console.error('Failed to fetch shift data', err);
      showToast('データの読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- toast ---
  const showToast = useCallback((msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // --- month navigation ---
  const prevMonth = useCallback(() => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  }, [month]);

  const nextMonth = useCallback(() => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  }, [month]);

  // --- cell editing ---
  const handleCellClick = useCallback((empId: string, date: string) => {
    setEditingCell({ empId, date });
  }, []);

  const handleShiftSelect = useCallback((shiftType: ShiftType | 'clear') => {
    if (!editingCell) return;
    const key = cellKey(editingCell.empId, editingCell.date);
    setLocalEdits(prev => {
      const next = new Map(prev);
      if (shiftType === 'clear') {
        next.delete(key);
      } else {
        next.set(key, shiftType);
      }
      return next;
    });
    setEditingCell(null);
  }, [editingCell, cellKey]);

  // --- auto generate ---
  const handleAutoGenerate = useCallback(() => {
    const hasExisting = schedules.length > 0 || localEdits.size > 0;
    if (hasExisting) {
      setConfirmDialog(true);
    } else {
      runAutoGenerate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules, localEdits]);

  const runAutoGenerate = useCallback(() => {
    setConfirmDialog(false);
    const result = generateShift({
      employees: filteredEmployees,
      conditions,
      preferences,
      settings: shiftSettings,
      year,
      month,
      approvedLeaves,
    });
    const newEdits = new Map<string, ShiftType>();
    result.forEach(r => {
      newEdits.set(cellKey(r.employeeId, r.scheduleDate), r.shiftType);
    });
    setLocalEdits(newEdits);
    showToast('シフトを自動生成しました', 'success');
  }, [filteredEmployees, conditions, preferences, shiftSettings, year, month, approvedLeaves, cellKey, showToast]);

  // --- save ---
  const handleSave = useCallback(async () => {
    if (!supabaseClient) return;
    setSaving(true);
    try {
      const merged = new Map<string, { employeeId: string; scheduleDate: string; shiftType: ShiftType }>();
      schedules.forEach(s => {
        merged.set(cellKey(s.employeeId, s.scheduleDate), {
          employeeId: s.employeeId,
          scheduleDate: s.scheduleDate,
          shiftType: s.shiftType,
        });
      });
      localEdits.forEach((st, key) => {
        const [empId, date] = key.split('|');
        merged.set(key, { employeeId: empId, scheduleDate: date, shiftType: st });
      });

      const rows = Array.from(merged.values()).map(r => ({
        employee_id: r.employeeId,
        schedule_date: r.scheduleDate,
        shift_type: r.shiftType,
        is_auto_generated: true,
        is_manually_edited: localEdits.has(cellKey(r.employeeId, r.scheduleDate)),
      }));

      if (rows.length === 0) {
        showToast('保存するデータがありません', 'error');
        setSaving(false);
        return;
      }

      const BATCH_SIZE = 500;
      for (let i = 0; i < rows.length; i += BATCH_SIZE) {
        const batch = rows.slice(i, i + BATCH_SIZE);
        const { error } = await supabaseClient
          .from('shift_schedules')
          .upsert(batch, { onConflict: 'employee_id,schedule_date' });
        if (error) throw error;
      }

      showToast('シフトを保存しました', 'success');
      await fetchData();
    } catch (err) {
      console.error('Failed to save shifts', err);
      showToast('保存に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  }, [schedules, localEdits, cellKey, fetchData, showToast]);

  // --- save condition ---
  const handleSaveCondition = useCallback(async (cond: EmployeeShiftCondition) => {
    if (!supabaseClient) return;
    setSaving(true);
    try {
      const row = {
        employee_id: cond.employeeId,
        shift_category: cond.shiftCategory,
        night_shift_max_per_month: cond.nightShiftMaxPerMonth,
        night_shift_allowed_days: cond.nightShiftAllowedDays,
        work_days_per_week: cond.workDaysPerWeek,
        regular_off_days: cond.regularOffDays,
        monthly_weekend_work: cond.monthlyWeekendWork,
        weekend_work_allowed_days: cond.weekendWorkAllowedDays,
        allowed_shift_types: cond.allowedShiftTypes,
        fixed_work_start: cond.fixedWorkStart,
        fixed_work_end: cond.fixedWorkEnd,
        work_hours_per_day: cond.workHoursPerDay,
        notes: cond.notes,
      };

      if (cond.id) {
        const { error } = await supabaseClient
          .from('employee_shift_conditions')
          .update(row)
          .eq('id', cond.id);
        if (error) throw error;
      } else {
        const { error } = await supabaseClient
          .from('employee_shift_conditions')
          .insert(row);
        if (error) throw error;
      }

      showToast('条件を保存しました', 'success');
      setConditionModalOpen(false);
      setEditingCondition(null);
      await fetchData();
    } catch (err) {
      console.error('Failed to save condition', err);
      showToast('条件の保存に失敗しました', 'error');
    } finally {
      setSaving(false);
    }
  }, [fetchData, showToast]);

  // --- save settings ---
  const handleSaveSettings = useCallback(async () => {
    if (!supabaseClient) return;
    setSettingsSaving(true);
    try {
      const row = {
        deadline_day: settingsForm.deadlineDay,
        min_night: settingsForm.minNight,
        min_early: settingsForm.minEarly,
        ideal_early: settingsForm.idealEarly,
        min_day: settingsForm.minDay,
        ideal_day: settingsForm.idealDay,
        target_job_type: settingsForm.targetJobType,
        monthly_off_days_31: settingsForm.monthlyOffDays31,
        monthly_off_days_other: settingsForm.monthlyOffDaysOther,
      };

      if (settingsForm.id) {
        const { error } = await supabaseClient
          .from('shift_settings')
          .update(row)
          .eq('id', settingsForm.id);
        if (error) throw error;
      } else {
        const { error } = await supabaseClient
          .from('shift_settings')
          .insert(row);
        if (error) throw error;
      }

      showToast('設定を保存しました', 'success');
      setShiftSettings({ ...settingsForm });
    } catch (err) {
      console.error('Failed to save settings', err);
      showToast('設定の保存に失敗しました', 'error');
    } finally {
      setSettingsSaving(false);
    }
  }, [settingsForm, showToast]);

  // --- daily summary ---
  const dailySummary = useMemo(() => {
    const summary: Record<string, Record<string, number>> = {};
    days.forEach(d => {
      const ds = formatDate(d);
      const counts: Record<string, number> = {};
      USED_SHIFT_TYPES.forEach(st => { counts[st] = 0; });
      counts['working'] = 0;
      filteredEmployees.forEach(emp => {
        const st = getScheduleForCell(emp.id, ds);
        if (st && counts[st] !== undefined) {
          counts[st] = (counts[st] || 0) + 1;
        }
        if (st && st !== 'dayoff' && st !== 'paid') {
          counts['working'] = (counts['working'] || 0) + 1;
        }
      });
      summary[ds] = counts;
    });
    return summary;
  }, [days, filteredEmployees, getScheduleForCell]);

  // --- preference map ---
  const preferenceMap = useMemo(() => {
    const m = new Map<string, PreferenceType>();
    preferences.forEach(p => {
      m.set(`${p.employeeId}|${p.preferenceDate}`, p.preferenceType);
    });
    return m;
  }, [preferences]);

  // --- condition map ---
  const conditionMap = useMemo(() => {
    const m = new Map<string, EmployeeShiftCondition>();
    conditions.forEach(c => m.set(c.employeeId, c));
    return m;
  }, [conditions]);

  const hasUnsavedChanges = localEdits.size > 0;

  // Helper: get warning color for summary row
  const getSummaryWarningClass = useCallback((shiftType: string, count: number): string => {
    if (shiftType === 'night') {
      if (count < shiftSettings.minNight) return 'bg-red-200 text-red-800 font-bold';
    }
    if (shiftType === 'early') {
      if (count < shiftSettings.minEarly) return 'bg-red-200 text-red-800 font-bold';
      if (count < shiftSettings.idealEarly) return 'bg-yellow-100 text-yellow-800';
    }
    if (shiftType === 'day') {
      if (count < shiftSettings.minDay) return 'bg-red-200 text-red-800 font-bold';
      if (count < shiftSettings.idealDay) return 'bg-yellow-100 text-yellow-800';
    }
    return '';
  }, [shiftSettings]);

  // =========================================================================
  // Render
  // =========================================================================
  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'success'
            ? 'bg-green-500 text-white'
            : 'bg-red-500 text-white'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Sub-tab navigation */}
      <div className="bg-white rounded-xl border border-gray-200 p-1 inline-flex gap-1">
        {([
          { id: 'schedule' as SubTab, label: 'シフト表', icon: Calendar },
          { id: 'preferences' as SubTab, label: '希望一覧', icon: ClipboardList },
          { id: 'conditions' as SubTab, label: '従業員条件', icon: Users },
          { id: 'settings' as SubTab, label: 'シフト設定', icon: SettingsIcon },
        ]).map(tab => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              subTab === tab.id
                ? 'bg-blue-500 text-white shadow'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ================================================================= */}
      {/* SUB-TAB 1: Schedule */}
      {/* ================================================================= */}
      {subTab === 'schedule' && (
        <div className="space-y-4">
          {/* Header: month selector + actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button onClick={prevMonth} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100">
                  <ChevronLeft size={16} />
                </button>
                <span className="text-lg font-bold text-gray-800">
                  {year}年{month}月
                </span>
                <button onClick={nextMonth} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100">
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleAutoGenerate}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-500 transition-all disabled:opacity-50"
                >
                  <Wand2 size={16} />
                  シフト自動生成
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !hasUnsavedChanges}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    hasUnsavedChanges
                      ? 'bg-blue-600 text-white hover:bg-blue-500 shadow'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Save size={16} />
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
            {hasUnsavedChanges && (
              <p className="text-xs text-orange-600 mt-2">
                * 未保存の変更があります ({localEdits.size}件)
              </p>
            )}
          </div>

          {/* Schedule table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                読み込み中...
              </div>
            ) : (
              <div className="max-h-[calc(100dvh-20rem)] overflow-auto overscroll-contain">
                <table className="w-full border-collapse text-xs">
                  <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="sticky left-0 z-20 bg-gray-50 border-r border-gray-200 px-3 py-2 text-left text-gray-600 font-medium min-w-[120px]">
                        従業員
                      </th>
                      {days.map(d => {
                        const dow = d.getDay();
                        const isSat = dow === 6;
                        const isSun = dow === 0;
                        return (
                          <th
                            key={formatDate(d)}
                            className={`px-1 py-1 text-center font-medium min-w-[36px] border-r border-gray-100 ${
                              isSun ? 'text-red-500' : isSat ? 'text-blue-500' : 'text-gray-600'
                            }`}
                          >
                            <div>{d.getDate()}</div>
                            <div className="text-[10px]">{WEEKDAY_LABELS[dow]}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map(emp => (
                      <tr key={emp.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                        <td className="sticky left-0 z-10 bg-white border-r border-gray-200 px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                          {emp.name}
                        </td>
                        {days.map(d => {
                          const ds = formatDate(d);
                          const st = getScheduleForCell(emp.id, ds);
                          const isEditing = editingCell?.empId === emp.id && editingCell?.date === ds;
                          const key = cellKey(emp.id, ds);
                          const isLocalEdit = localEdits.has(key);

                          return (
                            <td
                              key={ds}
                              className={`relative px-0.5 py-0.5 text-center border-r border-gray-100 cursor-pointer ${
                                isLocalEdit ? 'ring-1 ring-inset ring-orange-300' : ''
                              }`}
                              onClick={() => handleCellClick(emp.id, ds)}
                            >
                              {st ? (
                                <span className={`inline-block w-full rounded px-0.5 py-1 text-[11px] font-bold ${SHIFT_TYPE_COLORS[st]}`}>
                                  {SHIFT_TYPE_LABELS[st]}
                                </span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}

                              {/* Dropdown - only 6 used shift types */}
                              {isEditing && (
                                <div className="absolute top-full left-1/2 -translate-x-1/2 z-30 bg-white border border-gray-300 rounded-lg shadow-xl p-1 min-w-[80px]">
                                  {USED_SHIFT_TYPES.map(sType => (
                                    <button
                                      key={sType}
                                      onClick={(e) => { e.stopPropagation(); handleShiftSelect(sType); }}
                                      className={`block w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-100 ${SHIFT_TYPE_COLORS[sType]}`}
                                    >
                                      {SHIFT_TYPE_LABELS[sType]}
                                    </button>
                                  ))}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleShiftSelect('clear'); }}
                                    className="block w-full text-left px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-400 mt-1 border-t border-gray-100"
                                  >
                                    クリア
                                  </button>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {/* Summary rows - per shift type */}
                    {USED_SHIFT_TYPES.filter(st =>
                      st !== 'dayoff' && st !== 'paid'
                    ).map(st => (
                      <tr key={`summary-${st}`} className="bg-gray-50 border-b border-gray-100">
                        <td className="sticky left-0 z-10 bg-gray-50 border-r border-gray-200 px-3 py-1 text-gray-500 font-medium whitespace-nowrap">
                          <span className={`inline-block rounded px-1 py-0.5 text-[10px] ${SHIFT_TYPE_COLORS[st]}`}>
                            {SHIFT_TYPE_LABELS[st]}
                          </span>
                        </td>
                        {days.map(d => {
                          const ds = formatDate(d);
                          const count = dailySummary[ds]?.[st] ?? 0;
                          const warningClass = getSummaryWarningClass(st, count);
                          return (
                            <td key={ds} className={`px-0.5 py-1 text-center text-[10px] border-r border-gray-100 ${warningClass || 'text-gray-500'}`}>
                              {count > 0 ? count : ''}
                            </td>
                          );
                        })}
                      </tr>
                    ))}

                    {/* Total working count row */}
                    <tr className="bg-blue-50 border-b border-gray-200">
                      <td className="sticky left-0 z-10 bg-blue-50 border-r border-gray-200 px-3 py-1 text-blue-700 font-bold whitespace-nowrap text-[10px]">
                        勤務計
                      </td>
                      {days.map(d => {
                        const ds = formatDate(d);
                        const count = dailySummary[ds]?.['working'] ?? 0;
                        return (
                          <td key={ds} className="px-0.5 py-1 text-center text-[10px] text-blue-700 font-bold border-r border-gray-100">
                            {count > 0 ? count : ''}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* SUB-TAB 2: Preferences */}
      {/* ================================================================= */}
      {subTab === 'preferences' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <button onClick={prevMonth} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100">
                <ChevronLeft size={16} />
              </button>
              <span className="text-lg font-bold text-gray-800">
                {year}年{month}月 シフト希望一覧
              </span>
              <button onClick={nextMonth} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                読み込み中...
              </div>
            ) : preferences.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <ClipboardList size={40} className="mb-3 text-gray-300" />
                <p>この月のシフト希望はまだ提出されていません</p>
              </div>
            ) : (
              <div className="max-h-[calc(100dvh-20rem)] overflow-auto overscroll-contain">
                <table className="w-full border-collapse text-xs">
                  <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="sticky left-0 z-20 bg-gray-50 border-r border-gray-200 px-3 py-2 text-left text-gray-600 font-medium min-w-[120px]">
                        従業員
                      </th>
                      {days.map(d => {
                        const dow = d.getDay();
                        return (
                          <th
                            key={formatDate(d)}
                            className={`px-1 py-1 text-center font-medium min-w-[36px] border-r border-gray-100 ${
                              dow === 0 ? 'text-red-500' : dow === 6 ? 'text-blue-500' : 'text-gray-600'
                            }`}
                          >
                            <div>{d.getDate()}</div>
                            <div className="text-[10px]">{WEEKDAY_LABELS[dow]}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {activeEmployees.map(emp => {
                      const hasPref = preferences.some(p => p.employeeId === emp.id);
                      if (!hasPref) return null;
                      return (
                        <tr key={emp.id} className="border-b border-gray-100">
                          <td className="sticky left-0 z-10 bg-white border-r border-gray-200 px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                            {emp.name}
                          </td>
                          {days.map(d => {
                            const ds = formatDate(d);
                            const pref = preferenceMap.get(`${emp.id}|${ds}`);
                            return (
                              <td key={ds} className="px-0.5 py-0.5 text-center border-r border-gray-100">
                                {pref ? (
                                  <span className={`inline-block w-full rounded px-0.5 py-1 text-[11px] font-bold ${
                                    pref === 'paid'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : pref === 'dayoff'
                                      ? 'bg-gray-100 text-gray-600'
                                      : 'bg-purple-100 text-purple-800'
                                  }`}>
                                    {PREFERENCE_TYPE_LABELS[pref]}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* SUB-TAB 3: Employee Conditions */}
      {/* ================================================================= */}
      {subTab === 'conditions' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-800">従業員シフト条件</h2>
                <p className="text-sm text-gray-500 mt-1">各従業員の勤務区分・夜勤条件・勤務日数を管理します</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                読み込み中...
              </div>
            ) : (
              <div className="max-h-[calc(100dvh-20rem)] overflow-auto overscroll-contain">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-gray-600 font-medium">従業員</th>
                      <th className="px-4 py-3 text-left text-gray-600 font-medium">勤務区分</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">夜勤上限/月</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">夜勤可能曜日</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">週勤務日数</th>
                      <th className="px-4 py-3 text-left text-gray-600 font-medium">定休日</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">月間休出</th>
                      <th className="px-4 py-3 text-left text-gray-600 font-medium">許可シフト</th>
                      <th className="px-4 py-3 text-left text-gray-600 font-medium">固定勤務</th>
                      <th className="px-4 py-3 text-left text-gray-600 font-medium">備考</th>
                      <th className="px-4 py-3 text-center text-gray-600 font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map(emp => {
                      const cond = conditionMap.get(emp.id);
                      return (
                        <tr key={emp.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                          <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">{emp.name}</td>
                          <td className="px-4 py-3 text-gray-700">
                            {cond ? SHIFT_CATEGORY_LABELS[cond.shiftCategory] : (
                              <span className="text-gray-400">未設定</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-700">
                            {cond?.nightShiftMaxPerMonth !== null && cond?.nightShiftMaxPerMonth !== undefined
                              ? `${cond.nightShiftMaxPerMonth}回`
                              : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-700 text-xs">
                            {cond?.nightShiftAllowedDays && cond.nightShiftAllowedDays.length > 0
                              ? cond.nightShiftAllowedDays.map(d => DAY_OF_WEEK_LABELS[d]).join('')
                              : <span className="text-gray-400">全曜日</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-700">
                            {cond?.workDaysPerWeek !== null && cond?.workDaysPerWeek !== undefined
                              ? `${cond.workDaysPerWeek}日`
                              : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-700 text-xs">
                            {cond?.regularOffDays && cond.regularOffDays.length > 0
                              ? cond.regularOffDays.map(d => DAY_OF_WEEK_LABELS[d]).join(', ')
                              : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-700">
                            {cond ? `${cond.monthlyWeekendWork}回` : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {cond?.allowedShiftTypes && cond.allowedShiftTypes.length > 0
                              ? (
                                <div className="flex flex-wrap gap-1">
                                  {cond.allowedShiftTypes.map(st => (
                                    <span key={st} className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${SHIFT_TYPE_COLORS[st]}`}>
                                      {SHIFT_TYPE_LABELS[st]}
                                    </span>
                                  ))}
                                </div>
                              )
                              : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-700 text-xs">
                            {cond?.fixedWorkStart && cond?.fixedWorkEnd
                              ? `${cond.fixedWorkStart}~${cond.fixedWorkEnd}`
                              : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs max-w-[160px] truncate">
                            {cond?.notes ?? '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => {
                                setEditingCondition(
                                  cond ?? {
                                    id: '',
                                    employeeId: emp.id,
                                    shiftCategory: 'day_early_only',
                                    nightShiftMaxPerMonth: null,
                                    nightShiftAllowedDays: null,
                                    workDaysPerWeek: null,
                                    regularOffDays: null,
                                    monthlyWeekendWork: 0,
                                    weekendWorkAllowedDays: null,
                                    allowedShiftTypes: null,
                                    fixedWorkStart: null,
                                    fixedWorkEnd: null,
                                    workHoursPerDay: null,
                                    notes: null,
                                  }
                                );
                                setConditionModalOpen(true);
                              }}
                              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-blue-600 transition-all"
                              title={cond ? '条件を編集' : '条件を追加'}
                            >
                              {cond ? <Edit2 size={14} /> : <Plus size={14} />}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* SUB-TAB 4: Shift Settings */}
      {/* ================================================================= */}
      {subTab === 'settings' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="text-lg font-bold text-gray-800">シフト設定</h2>
            <p className="text-sm text-gray-500 mt-1">シフト自動生成に使用するパラメータを管理します</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="max-w-2xl space-y-6">
              {/* Staffing minimums */}
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">最低人数設定</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">夜勤 最低人数</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.minNight}
                      onChange={e => setSettingsForm(prev => ({ ...prev, minNight: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>{/* spacer */}</div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">早番 最低人数</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.minEarly}
                      onChange={e => setSettingsForm(prev => ({ ...prev, minEarly: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">早番 理想人数</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.idealEarly}
                      onChange={e => setSettingsForm(prev => ({ ...prev, idealEarly: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">日勤 最低人数</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.minDay}
                      onChange={e => setSettingsForm(prev => ({ ...prev, minDay: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">日勤 理想人数</label>
                    <input
                      type="number"
                      min={0}
                      value={settingsForm.idealDay}
                      onChange={e => setSettingsForm(prev => ({ ...prev, idealDay: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Off-days settings */}
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">公休日数設定</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">31日月の公休日数</label>
                    <input
                      type="number"
                      min={0}
                      max={31}
                      value={settingsForm.monthlyOffDays31}
                      onChange={e => setSettingsForm(prev => ({ ...prev, monthlyOffDays31: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">その他の月の公休日数</label>
                    <input
                      type="number"
                      min={0}
                      max={31}
                      value={settingsForm.monthlyOffDaysOther}
                      onChange={e => setSettingsForm(prev => ({ ...prev, monthlyOffDaysOther: Number(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Target job type */}
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">対象職種</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">対象職種</label>
                  <input
                    type="text"
                    value={settingsForm.targetJobType}
                    onChange={e => setSettingsForm(prev => ({ ...prev, targetJobType: e.target.value }))}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                    placeholder="介護"
                  />
                  <p className="text-xs text-gray-400 mt-1">シフト表に表示する従業員の職種を指定します</p>
                </div>
              </div>

              {/* Deadline */}
              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">希望締切日</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">毎月の締切日</label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={settingsForm.deadlineDay}
                    onChange={e => setSettingsForm(prev => ({ ...prev, deadlineDay: Number(e.target.value) || 15 }))}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">従業員がシフト希望を提出する締切日です</p>
                </div>
              </div>

              {/* Save button */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={handleSaveSettings}
                  disabled={settingsSaving}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all ${
                    settingsSaving ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 shadow'
                  }`}
                >
                  <Save size={16} />
                  {settingsSaving ? '保存中...' : '設定を保存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* Confirm Dialog: overwrite existing shifts */}
      {/* ================================================================= */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
              <AlertTriangle size={20} className="text-orange-500" />
              <h3 className="text-lg font-bold text-gray-800">確認</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700">
                既存のシフトデータがあります。自動生成で上書きしますか？
              </p>
              <p className="text-xs text-gray-500 mt-2">
                ※ 上書き後も「保存」ボタンを押すまではデータベースに反映されません
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
              <button
                onClick={() => setConfirmDialog(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
              >
                キャンセル
              </button>
              <button
                onClick={runAutoGenerate}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500 text-sm font-medium"
              >
                上書きする
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* Condition Edit Modal */}
      {/* ================================================================= */}
      {conditionModalOpen && editingCondition && (
        <ConditionEditModal
          condition={editingCondition}
          employeeName={filteredEmployees.find(e => e.id === editingCondition.employeeId)?.name ?? ''}
          saving={saving}
          onSave={handleSaveCondition}
          onClose={() => { setConditionModalOpen(false); setEditingCondition(null); }}
        />
      )}

      {/* Close dropdown on outside click */}
      {editingCell && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setEditingCell(null)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Condition Edit Modal (sub-component) - Full field support
// ---------------------------------------------------------------------------
interface ConditionEditModalProps {
  condition: EmployeeShiftCondition;
  employeeName: string;
  saving: boolean;
  onSave: (c: EmployeeShiftCondition) => void;
  onClose: () => void;
}

const ConditionEditModal: React.FC<ConditionEditModalProps> = ({
  condition,
  employeeName,
  saving,
  onSave,
  onClose,
}) => {
  const [form, setForm] = useState<EmployeeShiftCondition>({ ...condition });

  const updateField = <K extends keyof EmployeeShiftCondition>(
    key: K,
    value: EmployeeShiftCondition[K]
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const toggleDayOfWeek = (field: 'regularOffDays' | 'nightShiftAllowedDays' | 'weekendWorkAllowedDays', dow: DayOfWeek) => {
    setForm(prev => {
      const current = prev[field] ?? [];
      const next = current.includes(dow) ? current.filter(d => d !== dow) : [...current, dow];
      return { ...prev, [field]: next.length > 0 ? next : null };
    });
  };

  const toggleShiftType = (st: ShiftType) => {
    setForm(prev => {
      const current = prev.allowedShiftTypes ?? [];
      const next = current.includes(st) ? current.filter(s => s !== st) : [...current, st];
      return { ...prev, allowedShiftTypes: next.length > 0 ? next : null };
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
          <h3 className="text-lg font-bold text-gray-800">
            {employeeName} - シフト条件{condition.id ? '編集' : '追加'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Shift Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">勤務区分</label>
            <select
              value={form.shiftCategory}
              onChange={e => updateField('shiftCategory', e.target.value as ShiftCategory)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            >
              {(Object.keys(SHIFT_CATEGORY_LABELS) as ShiftCategory[]).map(cat => (
                <option key={cat} value={cat}>{SHIFT_CATEGORY_LABELS[cat]}</option>
              ))}
            </select>
          </div>

          {/* Night Shift Max */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">夜勤上限 (月)</label>
            <input
              type="number"
              min={0}
              value={form.nightShiftMaxPerMonth ?? ''}
              onChange={e => updateField('nightShiftMaxPerMonth', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="制限なしの場合は空欄"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>

          {/* Night Shift Allowed Days - Checkbox style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">夜勤可能曜日</label>
            <div className="flex flex-wrap gap-2">
              {DAY_OF_WEEK_ORDER.map(dow => {
                const active = form.nightShiftAllowedDays?.includes(dow);
                return (
                  <label key={dow} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!active}
                      onChange={() => toggleDayOfWeek('nightShiftAllowedDays', dow)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{DAY_OF_WEEK_LABELS[dow]}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1">未選択の場合は全曜日可</p>
          </div>

          {/* Work Days Per Week */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">週勤務日数</label>
            <input
              type="number"
              min={1}
              max={7}
              value={form.workDaysPerWeek ?? ''}
              onChange={e => updateField('workDaysPerWeek', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="制限なしの場合は空欄"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>

          {/* Regular Off Days - Checkbox style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">定休日</label>
            <div className="flex flex-wrap gap-2">
              {DAY_OF_WEEK_ORDER.map(dow => {
                const active = form.regularOffDays?.includes(dow);
                return (
                  <label key={dow} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!active}
                      onChange={() => toggleDayOfWeek('regularOffDays', dow)}
                      className="w-4 h-4 rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                    />
                    <span className="text-sm text-gray-700">{DAY_OF_WEEK_LABELS[dow]}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Monthly Weekend Work */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">月間休出回数</label>
            <input
              type="number"
              min={0}
              value={form.monthlyWeekendWork}
              onChange={e => updateField('monthlyWeekendWork', Number(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>

          {/* Weekend Work Allowed Days - Checkbox style */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">休出可能曜日</label>
            <div className="flex flex-wrap gap-2">
              {DAY_OF_WEEK_ORDER.map(dow => {
                const active = form.weekendWorkAllowedDays?.includes(dow);
                return (
                  <label key={dow} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!active}
                      onChange={() => toggleDayOfWeek('weekendWorkAllowedDays', dow)}
                      className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-700">{DAY_OF_WEEK_LABELS[dow]}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Allowed Shift Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">許可シフトタイプ</label>
            <div className="flex flex-wrap gap-2">
              {ALL_SHIFT_TYPES.map(st => {
                const active = form.allowedShiftTypes?.includes(st);
                return (
                  <button
                    key={st}
                    onClick={() => toggleShiftType(st)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                      active
                        ? SHIFT_TYPE_COLORS[st] + ' border-current'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {SHIFT_TYPE_LABELS[st]}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1">未選択の場合は日/早がデフォルト</p>
          </div>

          {/* Fixed Work Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">固定勤務開始</label>
              <input
                type="time"
                value={form.fixedWorkStart ?? ''}
                onChange={e => updateField('fixedWorkStart', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">固定勤務終了</label>
              <input
                type="time"
                value={form.fixedWorkEnd ?? ''}
                onChange={e => updateField('fixedWorkEnd', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Work Hours Per Day */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">1日の勤務時間 (h)</label>
            <input
              type="number"
              min={0}
              step={0.5}
              value={form.workHoursPerDay ?? ''}
              onChange={e => updateField('workHoursPerDay', e.target.value === '' ? null : Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">備考</label>
            <textarea
              value={form.notes ?? ''}
              onChange={e => updateField('notes', e.target.value || null)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm resize-none"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 sticky bottom-0 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm"
          >
            キャンセル
          </button>
          <button
            onClick={() => onSave(form)}
            disabled={saving}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all ${
              saving ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 shadow'
            }`}
          >
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftManagementTab;
